<template>
  <b-card no-body class="dash-panel dash-dues-chart-panel dash-dues-chart-panel--modern h-100">
    <b-card-header class="dash-panel__header dash-dues-chart-panel__header border-0 pb-0">
      <div class="dash-panel__heading dash-panel__heading--chart">
        <span class="dash-panel__icon dash-panel__icon--chart">
          <feather-icon icon="BarChart2Icon" size="18" />
        </span>
        <div class="dash-panel__heading-main">
          <b-card-title class="dash-panel__title mb-0">{{
            $t("Outstanding Dues")
          }}</b-card-title>
          <small class="dash-panel__subtitle">{{
            $t("Amount comparison")
          }}</small>
        </div>
        <div v-if="hasData" class="dash-chart-summary dash-chart-summary--dock">
          <span class="dash-chart-summary__label">{{ $t("Top fee type") }}</span>
          <strong :title="topFeeLabel">{{ topFeeLabel }}</strong>
        </div>
      </div>
    </b-card-header>

    <b-card-body class="pt-1 pb-1 dash-dues-chart-panel__body">
      <div ref="chartViewport" class="dash-chart-viewport-inner">
        <div v-if="!hasData" class="dash-chart-empty text-muted text-center py-4">
          {{ $t("No records found") }}
        </div>
        <div v-else-if="!chartInView" class="dash-chart-placeholder">
          <div class="dash-chart-placeholder__bars" aria-hidden="true">
            <span
              v-for="n in 5"
              :key="n"
              class="dash-chart-placeholder__bar"
              :style="{ animationDelay: `${n * 0.12}s` }"
            />
          </div>
          <span class="text-muted">{{ $t("Scroll to animate chart") }}</span>
        </div>
        <vue-apex-charts
          v-else
          :key="chartRenderKey()"
          type="bar"
          :height="chartHeight"
          :options="chartOptions"
          :series="chartSeries"
          class="dash-dues-apex-chart dash-dues-apex-chart--bars"
        />
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardTitle, BCardBody } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import chartInViewMixin from "./mixins/chartInViewMixin";
import { dashApexTooltipHorizontalBar } from "./utils/dashboardApexTooltip";

export default {
  mixins: [chartInViewMixin],
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    VueApexCharts,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chartHeight: 320,
      chartSeries: [{ name: "Amount", data: [] }],
      chartOptions: this.buildOptions([]),
    };
  },
  computed: {
    hasData() {
      return (
        Array.isArray(this.items) &&
        this.items.length > 0 &&
        this.items.some((r) => Number(r.amount) > 0)
      );
    },
    topFeeLabel() {
      if (!this.hasData) return "—";
      const top = [...this.items].sort(
        (a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0)
      )[0];
      return top && top.feeType ? top.feeType : "—";
    },
  },
  watch: {
    items: {
      deep: true,
      immediate: true,
      handler(list) {
        this.syncFromItems(list);
      },
    },
    chartInView(val) {
      if (val) this.$nextTick(() => this.applyChartHeight());
    },
  },
  mounted() {
    this.$nextTick(() => this.applyChartHeight());
  },
  methods: {
    palette() {
      return [
        "#7367f0",
        "#9e95f5",
        "#0ea5e9",
        "#14b8a6",
        "#28c76f",
        "#ff9f43",
        "#ea5455",
        "#82868b",
        "#a855f7",
      ];
    },
    gradientPartners() {
      return [
        "#5e50ee",
        "#7c6cf6",
        "#0284c7",
        "#0d9488",
        "#16a34a",
        "#ea580c",
        "#dc2626",
        "#64748b",
        "#9333ea",
      ];
    },
    barEntranceAnimation() {
      return {
        enabled: true,
        easing: "easeinout",
        speed: 1650,
        animateGradually: {
          enabled: true,
          delay: 85,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 750,
        },
      };
    },
    buildOptions(categories) {
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "inherit",
          background: "transparent",
          animations: this.barEntranceAnimation(),
          dropShadow: {
            enabled: true,
            top: 2,
            left: 0,
            blur: 6,
            opacity: 0.12,
            color: "#7367f0",
          },
        },
        theme: {
          mode: "light",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 14,
            borderRadiusApplication: "end",
            barHeight: "58%",
            distributed: true,
            dataLabels: {
              position: "top",
            },
          },
        },
        colors: this.palette(),
        fill: {
          type: "gradient",
          gradient: {
            shade: "dark",
            type: "horizontal",
            shadeIntensity: 0.35,
            gradientToColors: this.gradientPartners(),
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 0.92,
            stops: [0, 55, 100],
          },
        },
        dataLabels: {
          enabled: true,
          offsetX: 26,
          textAnchor: "start",
          style: {
            fontSize: "11px",
            fontWeight: 700,
            colors: ["#2f2b3d"],
          },
          background: {
            enabled: false,
          },
          dropShadow: {
            enabled: false,
          },
          formatter(val) {
            const n = Number(val);
            if (!n || Number.isNaN(n)) return "";
            if (n >= 1e6) return `Rs. ${(n / 1e6).toFixed(2)}M`;
            if (n >= 1e5) return `Rs. ${(n / 1e3).toFixed(0)}k`;
            return `Rs. ${n.toLocaleString()}`;
          },
        },
        grid: {
          borderColor: "rgba(115, 103, 240, 0.08)",
          strokeDashArray: 4,
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } },
          padding: { left: 4, right: 28, top: 6, bottom: 4 },
        },
        xaxis: {
          categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: { colors: "#a8aaae", fontSize: "11px", fontWeight: 600 },
            formatter(val) {
              const n = Number(val);
              if (Number.isNaN(n)) return val;
              if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
              if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
              return n.toLocaleString();
            },
          },
        },
        yaxis: {
          labels: {
            style: { colors: "#2f2b3d", fontSize: "11px", fontWeight: 700 },
            maxWidth: 220,
          },
        },
        states: {
          hover: {
            filter: {
              type: "lighten",
              value: 0.08,
            },
          },
          active: {
            filter: { type: "none" },
          },
        },
        tooltip: dashApexTooltipHorizontalBar(categories),
        legend: { show: false },
      };
    },
    applyChartHeight() {
      const rows = Array.isArray(this.items) ? this.items.length : 0;
      this.chartHeight = Math.min(720, Math.max(280, rows * 44 + 48));
    },
    syncFromItems(list) {
      const rows = Array.isArray(list) ? list : [];
      const categories = rows.map((el) => el.feeType);
      const data = rows.map((el) => Number(el.amount) || 0);

      this.chartOptions = this.buildOptions(categories);
      this.chartSeries = [{ name: "Amount", data }];
      this.applyChartHeight();
      this.bumpChartData();
      this.$nextTick(() => this.applyChartHeight());
    },
  },
};
</script>

<style scoped lang="scss">
.dash-dues-chart-panel--modern {
  border-radius: 22px !important;
  background: rgba(255, 255, 255, 0.62) !important;
  backdrop-filter: blur(16px) saturate(1.15);
  -webkit-backdrop-filter: blur(16px) saturate(1.15);
  border: 1px solid rgba(115, 103, 240, 0.12) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 14px 40px rgba(115, 103, 240, 0.08) !important;
}

.dash-dues-chart-panel__header {
  padding-bottom: 0.65rem !important;
}

.dash-panel__heading--chart {
  width: 100%;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 0.65rem;
}

.dash-chart-summary--dock {
  margin-left: auto;
  flex-shrink: 0;
  text-align: right;
  min-width: 8.5rem;
  max-width: 11.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 18px;
  background: linear-gradient(
    145deg,
    rgba(237, 231, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.9) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(115, 103, 240, 0.2);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 24px rgba(115, 103, 240, 0.12);

  strong {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
  }
}

.dash-chart-summary {
  text-align: right;
  max-width: 9rem;
  padding: 0.4rem 0.75rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(115, 103, 240, 0.15);
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.08);

  &__label {
    display: block;
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7367f0;
  }

  strong {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    color: #2f2b3d;
    line-height: 1.3;
    margin-top: 0.15rem;
  }
}

.dash-dues-chart-panel__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0.25rem 0.85rem 1rem !important;
}

.dash-chart-viewport-inner {
  flex: 1 1 auto;
  min-height: 280px;
  overflow: visible;
  position: relative;
  padding: 0.35rem 0.25rem 0.35rem 0;
  width: 100%;
}

.dash-dues-apex-chart {
  max-height: 100%;

  &--bars {
    animation: dashBarsReveal 0.5s ease-out both;
  }

  ::v-deep .apexcharts-datalabel,
  ::v-deep .apexcharts-datalabel-label,
  ::v-deep .apexcharts-datalabel-value {
    fill: #2f2b3d !important;
  }

  ::v-deep .apexcharts-data-labels rect {
    fill: #fff !important;
    stroke: #ebe9f1 !important;
    stroke-width: 1 !important;
  }
}

@keyframes dashBarsReveal {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dash-chart-empty {
  font-size: 0.9rem;
  font-weight: 600;
}

.dash-chart-placeholder {
  flex: 1 1 auto;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 1rem 1.5rem;
  width: 100%;
}

.dash-chart-placeholder__bars {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 100%;
  max-width: 280px;
}

.dash-chart-placeholder__bar {
  display: block;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(115, 103, 240, 0.15) 0%,
    rgba(115, 103, 240, 0.45) 50%,
    rgba(115, 103, 240, 0.15) 100%
  );
  background-size: 200% 100%;
  animation: dashBarShimmer 1.2s ease-in-out infinite;

  &:nth-child(1) {
    width: 92%;
  }
  &:nth-child(2) {
    width: 78%;
  }
  &:nth-child(3) {
    width: 65%;
  }
  &:nth-child(4) {
    width: 48%;
  }
  &:nth-child(5) {
    width: 36%;
  }
}

@keyframes dashBarShimmer {
  0% {
    background-position: 100% 0;
    opacity: 0.65;
  }
  50% {
    opacity: 1;
  }
  100% {
    background-position: -100% 0;
    opacity: 0.65;
  }
}
</style>
